import { createFileRoute } from "@tanstack/react-router";

import textLogo from "@/assets/images/text.png";
import { m } from "@/paraglide/messages.js";

const LAST_UPDATED = "September 7, 2026";

const SECTIONS = [
  {
    title: "Overview",
    body: "This site is the personal portfolio of Maciej Pyrc. It has no user accounts. The only personal data it collects is the email address you give when you subscribe to the newsletter, plus the technical data described below. This policy explains what is stored, why, and how to have it removed.",
  },
  {
    title: "Newsletter",
    body: "When you subscribe, your email address, the place on the site where you subscribed, and the time of subscription are stored. Emails are delivered through Resend, the email provider that processes your address on my behalf. Each issue contains an open-tracking image and click-tracked links, so I can see which issues and links get read. That data is tied to your subscription and is used only to understand what content is useful.",
  },
  {
    title: "Unsubscribing",
    body: "Every issue has a one-click unsubscribe link, and the unsubscribe page on this site works with the same link. Unsubscribing stops all further emails immediately. If you want your address and tracking history deleted entirely, reply to any issue or reach me through the links on the homepage and I will remove them.",
  },
  {
    title: "Technical data",
    body: "The site is hosted on Railway, whose servers keep short-lived request logs that include your IP address. Errors in the browser are reported to Sentry together with technical details of the request, such as browser version and the page you were on. Your theme choice and whether you dismissed the newsletter prompt are stored in your browser's local storage and never leave your device.",
  },
  {
    title: "Cookies and analytics",
    body: "The site does not set tracking cookies, does not use advertising networks, and does not run third-party analytics scripts.",
  },
  {
    title: "Sharing",
    body: "Your data is never sold or shared for marketing. It is processed only by the providers named above (Resend for email delivery, Railway for hosting, Sentry for error reporting) as needed to run the site and the newsletter.",
  },
  {
    title: "Retention",
    body: "Subscriber data is kept for as long as you are subscribed. After you unsubscribe, your address is kept only to make sure you are not emailed again, and is deleted on request. Server logs and error reports are kept for a short, provider-defined period.",
  },
  {
    title: "Your rights",
    body: "Under the GDPR you can ask what data is held about you, ask for it to be corrected or deleted, or withdraw your consent to the newsletter at any time. Contact me by replying to any newsletter issue or through the links on the homepage.",
  },
  {
    title: "Changes",
    body: "If this policy changes, the date at the top will be updated. Changes that affect how subscriber data is used will be announced in the newsletter.",
  },
] as const;

export const Route = createFileRoute("/(public)/privacy/")({
  component: PrivacyPage,
  loader: () => ({
    crumb: m.auth_privacy_crumb(),
  }),
});

function PrivacyPage() {
  return (
    <div className="min-h-svh px-6 py-10 md:px-10">
      <div className="mx-auto w-full max-w-2xl">
        <a href="/" className="mb-10 inline-flex items-center gap-2 font-medium">
          <img src={textLogo} alt={m.app_title()} className="h-12 dark:invert" />
        </a>

        <div className="space-y-1">
          <h1 className="text-3xl font-bold tracking-tight">{m.auth_legal_privacy()}</h1>
          <p className="text-sm text-muted-foreground">{m.auth_legal_last_updated({ date: LAST_UPDATED })}</p>
        </div>

        <div className="mt-10 space-y-8">
          {SECTIONS.map((section, index) => (
            <section key={section.title}>
              <h2 className="text-lg font-semibold">
                {index + 1}. {section.title}
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{section.body}</p>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
